import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import {
  Heart, Smile, Users, Compass, MessageCircle, CalendarHeart,
  Sprout, ArrowRight, Share2, Copy, Check, Sparkles,
  ShieldCheck, HandHeart, MapPin, Send, Mail, Linkedin, MessageSquare,
  Lock, Flag, AlertOctagon, ChevronDown, LifeBuoy,
} from "lucide-react";

const SHARE_URL = "https://plansobrio.com/promocion";
const SHARE_TITLE = "PlanSobrio";
const SHARE_TEXT =
  "Descubre PlanSobrio 🌱 — la app para conocer personas sobrias, hacer amigos, encontrar pareja o compartir planes sin alcohol ni drogas. Únete gratis:";
const enc = (s) => encodeURIComponent(s);

function useShare() {
  const [copied, setCopied] = useState(false);
  const openWin = (url) => window.open(url, "_blank", "noopener,noreferrer");
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${SHARE_TEXT} ${SHARE_URL}`);
      setCopied(true);
      toast.success("Enlace copiado — compártelo con quien quieras");
      setTimeout(() => setCopied(false), 2500);
    } catch { toast.error("No se pudo copiar el enlace"); }
  };
  const native = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: SHARE_TITLE, text: SHARE_TEXT, url: SHARE_URL }); return; }
      catch { /* cancelled */ }
    }
    copy();
  };
  return {
    native, copy, copied,
    whatsapp: () => openWin(`https://api.whatsapp.com/send?text=${enc(SHARE_TEXT + " " + SHARE_URL)}`),
    twitter: () => openWin(`https://twitter.com/intent/tweet?text=${enc(SHARE_TEXT)}&url=${enc(SHARE_URL)}`),
    facebook: () => openWin(`https://www.facebook.com/sharer/sharer.php?u=${enc(SHARE_URL)}&quote=${enc(SHARE_TEXT)}`),
    telegram: () => openWin(`https://t.me/share/url?url=${enc(SHARE_URL)}&text=${enc(SHARE_TEXT)}`),
    linkedin: () => openWin(`https://www.linkedin.com/sharing/share-offsite/?url=${enc(SHARE_URL)}`),
    reddit: () => openWin(`https://www.reddit.com/submit?url=${enc(SHARE_URL)}&title=${enc("PlanSobrio — comunidad para conocer personas sobrias")}`),
    email: () => openWin(`mailto:?subject=${enc("Descubre PlanSobrio")}&body=${enc(SHARE_TEXT + "\n\n" + SHARE_URL)}`),
    sms: () => openWin(`sms:?&body=${enc(SHARE_TEXT + " " + SHARE_URL)}`),
  };
}

const FAQS = [
  {
    q: "¿Existe una app de citas para personas sobrias en español?",
    a: "Sí. PlanSobrio es la primera app hispanohablante dedicada a personas que viven sin alcohol ni drogas. Disponible en 22 países de Latinoamérica y España, es gratuita en beta y permite conocer gente sobria para citas, amistad, apoyo y grupos con eventos reales sin alcohol.",
    aText: "PlanSobrio es la primera app hispana para conocer personas sobrias — citas, amistad, apoyo y grupos, gratis y en 22 países.",
  },
  {
    q: "¿Cómo conozco gente que no toma alcohol?",
    a: "Regístrate gratis en PlanSobrio, completa tu perfil eligiendo entre 4 modos (apoyo, amistad, amor o grupos) y descubre personas afines cercanas o en toda Latinoamérica. La app filtra por país, ciudad, edad, modo activo y planes en común — todos los perfiles comparten el compromiso de vivir sin alcohol ni drogas.",
    aText: "En PlanSobrio: te registras gratis, eliges modo (apoyo, amistad, amor o grupos), filtras por país o ciudad y encuentras personas que viven sin alcohol.",
  },
  {
    q: "¿PlanSobrio funciona en México, Argentina, Colombia y España?",
    a: "Sí. PlanSobrio está habilitada en 22 países hispanohablantes: Chile, Argentina, Colombia, México, Perú, España, Estados Unidos, Venezuela, Ecuador, Guatemala, Bolivia, Cuba, Rep. Dominicana, Honduras, Paraguay, El Salvador, Nicaragua, Costa Rica, Panamá, Uruguay, Puerto Rico y Guinea Ecuatorial. En cada país hay un grupo Comunidad con chat y reuniones online semanales.",
    aText: "Sí. PlanSobrio funciona en 22 países hispanohablantes con un grupo Comunidad y reuniones online por país.",
  },
  {
    q: "¿PlanSobrio es una app de citas?",
    a: "PlanSobrio es más que una app de citas. Puedes usarla para encontrar pareja sobria, pero también para hacer amigos, recibir apoyo mutuo entre pares o sumarte a grupos temáticos con eventos reales. Al registrarte eliges qué modos activar; tú decides el tipo de vínculo que quieres construir.",
    aText: "PlanSobrio es 4 apps en una: citas, amistad, apoyo y grupos. Tú eliges qué modos activar en tu perfil.",
  },
  {
    q: "¿Necesito estar en recuperación para registrarme?",
    a: "No. PlanSobrio está abierta a todas las personas mayores de 18 años que quieran conectar sin alcohol ni drogas — ya sea que estés en recuperación, lleves una vida sobria por elección (sobriedad consciente), practiques abstinencia temporal o simplemente prefieras planes donde el consumo no sea el foco.",
    aText: "No. Es abierta a mayores de 18 en recuperación, sobriedad consciente, abstinencia temporal o quien prefiere planes sin alcohol.",
  },
  {
    q: "¿PlanSobrio es gratis?",
    a: "Sí, es 100% gratis en la beta actual: registro, matches, chat, grupos, reuniones online y eventos. No hay suscripción ni compras dentro de la app. Cuando salgamos de beta habrá un plan gratuito indefinido y opcionales pagos para funciones avanzadas — nunca cobraremos por conectar con otras personas.",
    aText: "Sí, PlanSobrio es 100% gratis en beta. Registro, matches, chat, grupos y reuniones online sin costo.",
  },
  {
    q: "¿PlanSobrio reemplaza un tratamiento profesional?",
    a: "No. PlanSobrio es una comunidad de pares, no un tratamiento. Si necesitas ayuda profesional, la app te deriva a SinAdicciones.org y a las líneas verificadas de tu país. Muchas personas en PlanSobrio complementan la app con terapia, grupos como AA/NA o acompañamiento profesional.",
    aText: "No. PlanSobrio es una comunidad de pares, no reemplaza terapia. Deriva a SinAdicciones.org y helplines locales.",
  },
  {
    q: "¿Puedo usar PlanSobrio solo para hacer amigos?",
    a: "Sí. Activa solo los modos Amistad y Grupos en tu perfil y desactiva Amor si no buscas citas. Puedes cambiar los modos activos cuando quieras desde tu perfil.",
    aText: "Sí, activa solo Amistad y Grupos para hacer amigos sin sección de citas.",
  },
  {
    q: "¿Cómo se protegen mi ubicación y mi identidad?",
    a: "Nunca compartimos tu ubicación exacta. Usamos alias en lugar de nombre real, tu relación con las sustancias es privada por defecto (solo aparece como insignia si tú lo activas) y los reportes se procesan en minutos. La fecha de nacimiento se usa solo para verificar mayoría de edad, nunca se muestra.",
    aText: "Nunca compartimos ubicación exacta. Alias en vez de nombre. Fecha de nacimiento solo para verificar 18+.",
  },
  {
    q: "¿Qué pasa si alguien ofrece alcohol o drogas?",
    a: "Está estrictamente prohibido. Cada perfil, chat y grupo tiene botón de reporte. Los reportes graves son notificados al equipo en tiempo real y las cuentas que promuevan o vendan sustancias son suspendidas sin previo aviso.",
    aText: "Prohibido. Botón de reporte en todo. Cuentas que ofrezcan sustancias son suspendidas de inmediato.",
  },
  {
    q: "¿Quién está detrás de PlanSobrio?",
    a: (
      <>
        PlanSobrio nace de <b>Nelson González</b>, fundador de{" "}
        <a href="https://sinadicciones.org" target="_blank" rel="noopener noreferrer" className="text-[#8B5CF6] font-semibold underline underline-offset-2 hover:text-[#6D28D9]">Sinadicciones.org</a> — organización dedicada a acompañar procesos de recuperación de adicciones, difundir información y visibilizar historias reales. PlanSobrio es la extensión digital de ese trabajo: llevar la comunidad sobria al espacio donde hoy la mayoría busca conectar.{" "}
        <a href="https://sinadicciones.org" target="_blank" rel="noopener noreferrer" className="text-[#8B5CF6] font-semibold underline underline-offset-2 hover:text-[#6D28D9]">Conoce Sinadicciones.org →</a>
      </>
    ),
    aText: "PlanSobrio nace de Nelson González, fundador de Sinadicciones.org — organización que acompaña procesos de recuperación. Más en https://sinadicciones.org",
  },
];

function Faq() {
  const [open, setOpen] = useState(0);
  return (
    <section className="mx-auto max-w-4xl px-5 sm:px-8 py-14" data-testid="promo-faq">
      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">Preguntas frecuentes</p>
      <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black tracking-tight">
        Lo que <span className="ps-gradient-text">quieres saber</span> antes de entrar.
      </h2>
      <div className="mt-8 space-y-2.5">
        {FAQS.map((f, i) => {
          const isOpen = open === i;
          return (
            <div
              key={f.q}
              data-testid={`faq-${i}`}
              className={`rounded-2xl border transition ${isOpen ? "border-slate-300 bg-slate-50" : "border-slate-200 bg-white"}`}
            >
              <button
                onClick={() => setOpen(isOpen ? -1 : i)}
                className="w-full flex items-center justify-between gap-4 p-5 text-left"
                data-testid={`faq-toggle-${i}`}
              >
                <span className="font-display font-black text-[15px] sm:text-base text-slate-900 leading-snug">{f.q}</span>
                <ChevronDown size={18} strokeWidth={2.4} className={`shrink-0 text-slate-500 transition-transform ${isOpen ? "rotate-180" : ""}`}/>
              </button>
              {isOpen && (
                <div className="px-5 pb-5 text-sm text-slate-600 leading-relaxed" data-testid={`faq-answer-${i}`}>
                  {f.a}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default function Promocion() {
  const s = useShare();

  const TITLE = "App de citas sin alcohol y comunidad sobria | PlanSobrio";
  const DESC = "PlanSobrio es la app gratuita para conocer personas sobrias en Chile y Latinoamérica: citas, amistad, apoyo y planes sin alcohol ni drogas. Únete gratis.";
  const HREFLANGS = [
    { lang: "es",       href: "https://plansobrio.com/promocion" },
    { lang: "es-CL",    href: "https://plansobrio.com/promocion?pais=CL" },
    { lang: "es-MX",    href: "https://plansobrio.com/promocion?pais=MX" },
    { lang: "es-AR",    href: "https://plansobrio.com/promocion?pais=AR" },
    { lang: "es-CO",    href: "https://plansobrio.com/promocion?pais=CO" },
    { lang: "es-PE",    href: "https://plansobrio.com/promocion?pais=PE" },
    { lang: "es-ES",    href: "https://plansobrio.com/promocion?pais=ES" },
    { lang: "x-default", href: "https://plansobrio.com/promocion" },
  ];

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "PlanSobrio",
      alternateName: "PlanSobrio — app de citas sin alcohol",
      applicationCategory: "SocialNetworkingApplication",
      operatingSystem: "Web, iOS, Android",
      url: "https://plansobrio.com/",
      description: DESC,
      inLanguage: "es",
      audience: { "@type": "PeopleAudience", suggestedMinAge: 18 },
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD", availability: "https://schema.org/InStock" },
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "PlanSobrio",
      url: "https://plansobrio.com/",
      logo: "https://plansobrio.com/favicon-512.png",
      description: "Comunidad hispana para conocer personas que viven sin alcohol ni drogas.",
      sameAs: ["https://sinadicciones.org"],
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Inicio", item: "https://plansobrio.com/" },
        { "@type": "ListItem", position: 2, name: "Promoción", item: "https://plansobrio.com/promocion" },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.aText || (typeof f.a === "string" ? f.a : "") },
      })),
    },
  ];

  return (
    <>
      <Helmet prioritizeSeoTags>
        <html lang="es"/>
        <title>{TITLE}</title>
        <meta name="description" content={DESC}/>
        <link rel="canonical" href="https://plansobrio.com/promocion"/>
        {HREFLANGS.map((h) => (
          <link key={h.lang} rel="alternate" hrefLang={h.lang} href={h.href}/>
        ))}
        <meta property="og:type" content="website"/>
        <meta property="og:title" content={TITLE}/>
        <meta property="og:description" content={DESC}/>
        <meta property="og:url" content="https://plansobrio.com/promocion"/>
        <meta property="og:image" content="https://plansobrio.com/og-promocion.jpg"/>
        <meta property="og:locale" content="es"/>
        <meta name="twitter:card" content="summary_large_image"/>
        <meta name="twitter:title" content={TITLE}/>
        <meta name="twitter:description" content={DESC}/>
        <meta name="twitter:image" content="https://plansobrio.com/og-promocion.jpg"/>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
    <div className="min-h-screen bg-white text-[#0F172A] font-sans overflow-x-hidden" data-testid="promo-page">
      {/* Ambient glow orbs */}
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full opacity-30 blur-3xl" style={{ background: "radial-gradient(closest-side, #FF6B5E, transparent 70%)" }}/>
        <div className="absolute top-1/2 -right-32 h-[520px] w-[520px] rounded-full opacity-25 blur-3xl" style={{ background: "radial-gradient(closest-side, #8B5CF6, transparent 70%)" }}/>
        <div className="absolute bottom-0 left-1/4 h-[420px] w-[420px] rounded-full opacity-20 blur-3xl" style={{ background: "radial-gradient(closest-side, #4ADE80, transparent 70%)" }}/>
      </div>

      {/* Nav */}
      <nav className="mx-auto max-w-6xl px-5 sm:px-8 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="promo-logo">
          <div className="w-10 h-10 rounded-2xl ps-gradient flex items-center justify-center shadow-lg shadow-purple-500/20">
            <Sprout size={20} className="text-white" strokeWidth={2}/>
          </div>
          <span className="font-display text-xl font-black tracking-tight">PlanSobrio</span>
        </Link>
        <div className="flex items-center gap-2">
          <Link
            to="/blog"
            data-testid="promo-nav-blog"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition"
          >
            Blog
          </Link>
          <Link
            to="/convenios"
            data-testid="promo-nav-convenios"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition"
          >
            Convenios
          </Link>
          <button
            onClick={s.native}
            data-testid="promo-nav-share"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold hover:border-slate-300 hover:bg-slate-50 transition"
          >
            <Share2 size={15} strokeWidth={2}/> Compartir
          </button>
          <Link
            to="/registro?utm_source=promocion&utm_medium=organic"
            data-testid="promo-nav-cta"
            className="inline-flex items-center gap-1.5 rounded-full ps-gradient text-white px-4 py-2 text-sm font-bold shadow-md shadow-purple-500/30 hover:opacity-95 transition"
          >
            Crear cuenta gratis <ArrowRight size={15} strokeWidth={2.4}/>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <header className="mx-auto max-w-6xl px-5 sm:px-8 pt-10 sm:pt-16 pb-14">
        <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 backdrop-blur px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-slate-600" data-testid="promo-hero-badge">
          <span className="w-1.5 h-1.5 rounded-full bg-[#4ADE80]"/> Beta abierta · 22 países · Gratis
        </div>
        <h1
          data-testid="promo-hero-title"
          className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.02] tracking-tight max-w-4xl"
        >
          La app para <span className="ps-gradient-text">conocer personas que viven sin alcohol</span> ni drogas.
        </h1>
        <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed" data-testid="promo-hero-sub">
          <b className="text-slate-900">PlanSobrio</b> es la primera app hispana para conocer gente sobria: <b className="text-slate-900">citas, amistad, apoyo entre pares y grupos con eventos reales sin alcohol</b>. Gratis, disponible en <b className="text-slate-900">22 países</b> de Latinoamérica y España. Ya sea que estés en recuperación, elijas sobriedad consciente o simplemente prefieras planes donde el trago no sea el centro — aquí encuentras personas que buscan lo mismo.
        </p>
        <p className="mt-3 text-xs text-slate-400" data-testid="promo-updated">Actualizado: Febrero 2026</p>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Link
            to="/registro?utm_source=promocion&utm_medium=organic"
            data-testid="promo-hero-cta"
            className="inline-flex items-center gap-2 rounded-full ps-gradient text-white px-6 py-3.5 text-[15px] font-bold shadow-xl shadow-purple-500/30 hover:opacity-95 transition"
          >
            Crear mi cuenta gratis <ArrowRight size={17} strokeWidth={2.4}/>
          </Link>
          <a
            href="#como-funciona"
            data-testid="promo-hero-secondary"
            className="inline-flex items-center gap-2 rounded-full border-2 border-slate-900 bg-white px-6 py-3 text-[15px] font-bold hover:bg-slate-900 hover:text-white transition"
          >
            Conocer cómo funciona
          </a>
        </div>

        {/* Bloque de respuesta autocontenido — óptimo para extracción por IA */}
        <div className="mt-10 rounded-3xl border border-slate-200 bg-white/70 backdrop-blur p-6 max-w-3xl" data-testid="promo-answer-block">
          <h2 className="font-display text-lg font-black tracking-tight mb-2">¿Qué es una app de citas sin alcohol?</h2>
          <p className="text-slate-700 leading-relaxed">
            Es una aplicación pensada para personas que quieren conocer, salir o hacer amigos <b>sin que el alcohol o las drogas sean parte del vínculo</b>. En español, la primera con este enfoque es <b>PlanSobrio</b>: gratuita, con 4 modos (citas, amistad, apoyo, grupos) y disponible en 22 países hispanohablantes. Todos los perfiles comparten el compromiso de una vida sin alcohol.
          </p>
        </div>

        <p className="mt-6 text-xs text-slate-500 flex items-center gap-2 flex-wrap" data-testid="promo-hero-note">
          <ShieldCheck size={13} strokeWidth={2} className="text-[#4ADE80]"/>
          Solo para mayores de 18 años · Comunidad moderada · Reglas claras de convivencia
        </p>
      </header>

      {/* Intent selector */}
      <section className="mx-auto max-w-6xl px-5 sm:px-8 py-14" data-testid="promo-intencion">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">Elige tu punto de partida</p>
        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black tracking-tight">
          ¿Qué buscas hoy?
        </h2>
        <p className="mt-3 text-slate-600 max-w-2xl">No todas las personas llegan buscando lo mismo. Elige el tipo de vínculo que quieres construir — puedes cambiarlo cuando quieras.</p>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { key: "apoyo", icon: HandHeart, title: "Alguien con quien hablar", desc: "Encuentra personas que puedan comprender tu proceso. Un espacio para escuchar y ser escuchado, sin juicios.", accent: "#FF6B5E", grad: "from-rose-100 to-white" },
            { key: "amistad", icon: Smile, title: "Nuevas amistades", desc: "Conoce gente para compartir caminatas, cafés, ferias, cine, deporte y conversaciones reales.", accent: "#F59E0B", grad: "from-amber-50 to-white" },
            { key: "amor", icon: Heart, title: "Una relación", desc: "Personas interesadas en construir un vínculo donde importa más la conexión que la copa.", accent: "#8B5CF6", grad: "from-fuchsia-100 to-white" },
            { key: "grupos", icon: Users, title: "Grupos y planes", desc: "Únete a comunidades y encuentros de café, deporte, naturaleza, arte y cultura.", accent: "#4ADE80", grad: "from-emerald-50 to-white" },
          ].map((it) => (
            <Link
              key={it.key}
              to={`/registro?intencion=${it.key}`}
              data-testid={`promo-intencion-${it.key}`}
              className={`relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br ${it.grad} p-6 hover:border-slate-300 hover:-translate-y-0.5 transition group`}
            >
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ background: it.accent + "20", color: it.accent }}>
                  <it.icon size={22} strokeWidth={2}/>
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-lg font-black">{it.title}</h3>
                  <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{it.desc}</p>
                </div>
                <ArrowRight size={18} strokeWidth={2} className="text-slate-400 group-hover:text-slate-900 group-hover:translate-x-0.5 transition"/>
              </div>
              <div className="absolute -bottom-6 -right-6 w-24 h-24 rounded-full opacity-10 blur-2xl" style={{ background: it.accent }}/>
            </Link>
          ))}
        </div>
      </section>

      {/* Cómo funciona */}
      <section id="como-funciona" className="mx-auto max-w-6xl px-5 sm:px-8 py-14 scroll-mt-16">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">Cómo funciona PlanSobrio</p>
        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black tracking-tight">
          Match por plan, <span className="ps-gradient-text">no solo por foto</span>.
        </h2>
        <p className="mt-3 text-slate-600 max-w-2xl">Conecta por las cosas que realmente podrían hacer juntos: tomar un café, hacer una caminata, recorrer una feria, ir al cine o conversar online.</p>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { n: "01", icon: Sprout, title: "Crea tu perfil", desc: "Cuéntanos qué tipo de conexión buscas, qué planes disfrutas y cómo prefieres relacionarte con la comunidad." },
            { n: "02", icon: Compass, title: "Descubre personas afines", desc: "Encuentra personas cercanas con intereses, experiencias y planes compatibles." },
            { n: "03", icon: MessageCircle, title: "Conversen y armen un plan", desc: "Chatea con calma, propón un plan y conéctate de forma segura, a tu ritmo." },
          ].map((step) => (
            <div key={step.n} data-testid={`promo-paso-${step.n}`} className="rounded-3xl border border-slate-200 bg-white p-6 hover:border-slate-300 transition">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-black text-slate-400 tracking-widest">{step.n}</span>
                <div className="w-10 h-10 rounded-full ps-gradient flex items-center justify-center text-white">
                  <step.icon size={18} strokeWidth={2}/>
                </div>
              </div>
              <h3 className="mt-4 font-display text-lg font-black">{step.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Para quién es */}
      <section className="mx-auto max-w-6xl px-5 sm:px-8 py-14">
        <div className="rounded-[32px] border border-slate-200 bg-gradient-to-br from-white via-white to-slate-50 p-8 sm:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">Para quién es</p>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black tracking-tight" data-testid="promo-para-quien">
                Aquí no tienes que <span className="ps-gradient-text">explicar por qué no tomas</span>.
              </h2>
              <p className="mt-3 text-slate-600 leading-relaxed">
                PlanSobrio está abierto a todas las personas mayores de 18 años que quieran conectar en un entorno sin alcohol ni drogas.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  "Estás en proceso de recuperación y quieres vincularte con personas que entienden.",
                  "Vives sin alcohol ni drogas desde hace tiempo y buscas pares.",
                  "Nunca tuviste problemas con dependencias — simplemente prefieres planes sanos.",
                  "Quieres conocer gente sin tener que ir a fiestas ni bares.",
                ].map((li) => (
                  <li key={li} className="flex items-start gap-3">
                    <span className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "#4ADE8020", color: "#16A34A" }}>
                      <Check size={12} strokeWidth={3}/>
                    </span>
                    <span className="text-sm text-slate-700 leading-relaxed">{li}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: MessageCircle, label: "Chatear online", desc: "Sin presión, empieza suave." },
                { icon: MapPin, label: "Grupos por zona", desc: "Encuéntralos cerca." },
                { icon: Sparkles, label: "Planes reales", desc: "Café, naturaleza, cine, feria." },
                { icon: ShieldCheck, label: "Comunidad moderada", desc: "Reglas claras y reportes." },
              ].map((c) => (
                <div key={c.label} className="rounded-2xl border border-slate-200 bg-white p-4 hover:border-slate-300 hover:-translate-y-0.5 transition" data-testid={`promo-feature-${c.label.split(" ")[0].toLowerCase()}`}>
                  <c.icon size={18} strokeWidth={2} className="text-[#8B5CF6]"/>
                  <p className="mt-2 font-display font-black text-[15px] leading-tight">{c.label}</p>
                  <p className="mt-1 text-[12px] text-slate-500">{c.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Seguridad */}
      <section className="mx-auto max-w-6xl px-5 sm:px-8 py-14" data-testid="promo-seguridad">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">Comunidad más segura</p>
        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black tracking-tight">
          Herramientas para <span className="ps-gradient-text">cuidarnos</span> entre todos.
        </h2>
        <p className="mt-3 text-slate-600 max-w-2xl">Ninguna app puede garantizar seguridad absoluta, pero sí podemos entregarte las mejores herramientas para decidir con calma con quién y cuándo conectar.</p>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[
            { icon: Lock, title: "Información sensible protegida", desc: "Tu relación con las sustancias es privada por defecto. Solo se muestra si tú lo activas." },
            { icon: Flag, title: "Reporte y bloqueo en un clic", desc: "Cada perfil, chat y grupo tiene botones claros de reporte y bloqueo. Revisamos cada caso." },
            { icon: AlertOctagon, title: "Cero tolerancia con sustancias", desc: "Prohibido ofrecer, promover o vender alcohol o drogas. Suspensión inmediata sin previo aviso." },
            { icon: ShieldCheck, title: "Correo verificado al registrarte", desc: "Verificamos correo y estamos trabajando en verificación de identidad para las próximas versiones." },
            { icon: LifeBuoy, title: "Canales de ayuda visibles", desc: "Enlaces a líneas de crisis y recursos profesionales en toda la app. Nunca reemplazamos terapia." },
            { icon: Users, title: "Reglas de convivencia claras", desc: "Sin acoso, sin discriminación, sin discursos de odio. Comunidad amable o no comunidad." },
          ].map((c) => (
            <div key={c.title} data-testid={`seg-${c.title.split(" ")[0].toLowerCase()}`} className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-slate-300 transition">
              <div className="w-10 h-10 rounded-2xl bg-slate-900 text-white flex items-center justify-center">
                <c.icon size={18} strokeWidth={2}/>
              </div>
              <p className="mt-3 font-display font-black text-[15px] leading-tight">{c.title}</p>
              <p className="mt-1.5 text-[13px] text-slate-600 leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <Faq/>

      {/* Fundador */}
      <section className="mx-auto max-w-6xl px-5 sm:px-8 py-14" data-testid="promo-fundador">
        <div className="rounded-[32px] border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-white p-8 sm:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8 items-center">
            <div className="mx-auto lg:mx-0">
              <div className="w-44 h-44 rounded-3xl ps-gradient p-[3px] shadow-2xl shadow-purple-500/20">
                <img
                  src="https://customer-assets.emergentagent.com/job_comunidad-sobria/artifacts/b1cxt8p1_IMG_3198.jpeg"
                  alt="Nelson González, fundador de PlanSobrio y Sinadicciones.org"
                  loading="lazy"
                  decoding="async"
                  data-testid="fundador-photo"
                  className="w-full h-full rounded-[22px] object-cover"
                />
              </div>
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">Detrás del proyecto</p>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black tracking-tight" data-testid="promo-fundador-title">
                Nelson González — <span className="ps-gradient-text">fundador de PlanSobrio</span>
              </h2>
              <p className="mt-4 text-slate-700 leading-relaxed">
                Persona en recuperación, padre, hijo y hermano. Fundador de{" "}
                <a href="https://sinadicciones.org" target="_blank" rel="noopener noreferrer" className="font-semibold text-[#8B5CF6] underline underline-offset-2 hover:text-[#6D28D9]" data-testid="fundador-sinadicciones-link">
                  Sinadicciones.org
                </a>{" "}
                y autor del libro <em>&ldquo;El principio del mapa inverso&rdquo;</em>. PlanSobrio nace de años acompañando procesos de recuperación en Chile — de la convicción de que <b>los vínculos sanos son el mejor tratamiento de largo plazo</b> y de que la vida sobria merece un espacio propio en internet, más allá de los grupos de ayuda tradicionales.
              </p>
              <p className="mt-3 text-slate-600 leading-relaxed">
                &ldquo;No se trata de la vida que dejaste. Se trata de la vida que puedes construir ahora.&rdquo;
              </p>
              <div className="mt-6 flex flex-wrap gap-2.5">
                <a
                  href="https://sinadicciones.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="fundador-cta-org"
                  className="inline-flex items-center gap-2 rounded-full border-2 border-slate-900 bg-white px-5 py-2.5 text-sm font-bold hover:bg-slate-900 hover:text-white transition"
                >
                  Conocer Sinadicciones.org <ArrowRight size={15} strokeWidth={2.4}/>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Compartir */}
      <section className="mx-auto max-w-6xl px-5 sm:px-8 py-14 scroll-mt-16" id="compartir">
        <div className="rounded-[32px] ps-gradient p-8 sm:p-12 text-white shadow-2xl shadow-purple-500/25">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] opacity-80">Ayúdanos a crecer</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black tracking-tight max-w-2xl" data-testid="promo-share-title">
              Alguien que conoces necesita saber que existimos.
            </h2>
            <p className="mt-3 opacity-90 leading-relaxed max-w-2xl">
              Comparte PlanSobrio directamente en tu red favorita. Cada persona que llega hace la comunidad más fuerte.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
            {[
              { key: "whatsapp", label: "WhatsApp", onClick: s.whatsapp, bg: "#25D366", char: "W" },
              { key: "telegram", label: "Telegram", onClick: s.telegram, bg: "#26A5E4", icon: Send },
              { key: "x", label: "X · Twitter", onClick: s.twitter, bg: "#000000", char: "𝕏" },
              { key: "facebook", label: "Facebook", onClick: s.facebook, bg: "#1877F2", char: "f" },
              { key: "linkedin", label: "LinkedIn", onClick: s.linkedin, bg: "#0A66C2", icon: Linkedin },
              { key: "reddit", label: "Reddit", onClick: s.reddit, bg: "#FF4500", char: "r" },
              { key: "email", label: "Correo", onClick: s.email, bg: "#0F172A", icon: Mail },
              { key: "sms", label: "SMS", onClick: s.sms, bg: "#334155", icon: MessageSquare },
            ].map((r) => (
              <button
                key={r.key}
                onClick={r.onClick}
                data-testid={`promo-share-${r.key}`}
                className="group flex flex-col items-center justify-center gap-2 rounded-2xl bg-white text-[#0F172A] p-4 font-bold text-[13px] hover:-translate-y-0.5 transition shadow-md"
              >
                <span
                  className="w-11 h-11 rounded-2xl flex items-center justify-center text-white text-lg font-black shadow-sm"
                  style={{ background: r.bg }}
                >
                  {r.icon ? <r.icon size={20} strokeWidth={2}/> : r.char}
                </span>
                <span>{r.label}</span>
              </button>
            ))}
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <button
              onClick={s.native}
              data-testid="promo-share-native"
              className="flex items-center justify-center gap-2 rounded-2xl bg-white/15 backdrop-blur border border-white/30 text-white px-5 py-3.5 font-bold text-[14px] hover:bg-white/20 transition"
            >
              <Share2 size={17} strokeWidth={2.2}/> Más opciones del sistema
            </button>
            <button
              onClick={s.copy}
              data-testid="promo-share-copy"
              className="flex items-center justify-center gap-2 rounded-2xl bg-white text-[#0F172A] px-5 py-3.5 font-bold text-[14px] hover:bg-slate-50 transition"
            >
              {s.copied ? <Check size={17} strokeWidth={2.4}/> : <Copy size={17} strokeWidth={2.2}/>}
              {s.copied ? "¡Enlace copiado!" : "Copiar enlace"}
            </button>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-6xl px-5 sm:px-8 py-14">
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight leading-[1.05]">
            Tu próximo café <span className="ps-gradient-text">puede empezar aquí</span>.
          </h2>
          <p className="mt-4 text-slate-600 text-lg">
            Conoce a alguien para conversar, hacer un plan o construir algo nuevo. Sin alcohol, sin drogas y sin tener que dar explicaciones.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link
              to="/registro"
              data-testid="promo-final-cta"
              className="inline-flex items-center gap-2 rounded-full ps-gradient text-white px-8 py-4 text-[16px] font-bold shadow-xl shadow-purple-500/30 hover:opacity-95 transition"
            >
              Crear mi cuenta gratis <ArrowRight size={18} strokeWidth={2.4}/>
            </Link>
            <Link
              to="/login"
              data-testid="promo-final-login"
              className="inline-flex items-center gap-2 rounded-full border-2 border-slate-900 bg-white text-slate-900 px-8 py-[14px] text-[16px] font-bold hover:bg-slate-900 hover:text-white transition"
            >
              Ya tengo cuenta
            </Link>
          </div>
          <p className="mt-6 text-xs text-slate-500">Toma menos de 2 minutos. Tú decides qué información compartir.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="mx-auto max-w-6xl px-5 sm:px-8 py-10 border-t border-slate-200">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between text-sm text-slate-500">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-xl ps-gradient flex items-center justify-center">
              <Sprout size={14} className="text-white" strokeWidth={2.2}/>
            </div>
            <span className="font-display font-black text-slate-900">PlanSobrio</span>
            <span className="opacity-70 inline-flex items-center gap-1">
              · hecho con <Heart size={12} strokeWidth={0} fill="#FF6B5E" className="inline-block"/> desde{" "}
              <a href="https://sinadicciones.org" target="_blank" rel="noopener noreferrer" data-testid="footer-sinadicciones" className="font-semibold text-slate-900 hover:text-[#8B5CF6] transition underline underline-offset-2 decoration-slate-300">
                Sinadicciones.org
              </a>
            </span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/blog" className="hover:text-slate-900 transition" data-testid="promo-footer-blog">Blog</Link>
            <Link to="/convenios" className="hover:text-slate-900 transition" data-testid="promo-footer-convenios">Convenios</Link>
            <Link to="/terminos" className="hover:text-slate-900 transition">Términos</Link>
            <Link to="/privacidad" className="hover:text-slate-900 transition">Privacidad</Link>
            <a href="mailto:contacto@sinadicciones.org" className="hover:text-slate-900 transition">Contacto</a>
          </div>
        </div>
        <p className="mt-6 text-xs text-slate-400 max-w-3xl">
          PlanSobrio es una comunidad de conexión sana. No reemplaza tratamiento profesional ni atención de urgencia. Si estás en crisis, contacta a <a href="tel:6003607777" className="underline hover:text-slate-700">Salud Responde 600 360 7777</a> (Chile) o al helpline de tu país.
        </p>
      </footer>
    </div>
    </>
  );
}
